import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { CheckCircle, Star, Zap, Crown } from 'lucide-react';
import { CreemPaymentButton, UpgradeCard } from '../components/payment/CreemPaymentButton';
import { useProAccess } from '../hooks/useProAccess';
import { usePaymentService } from '../services/paymentService';
import { useUIStore } from '../store';

export const PricingPage: React.FC = () => {
  const { currentPlan, isLoading, userId, refreshAccess } = useProAccess();
  const { handlePaymentSuccess, handlePaymentCancel, getPaymentStatusMessage } = usePaymentService();
  const { addNotification } = useUIStore();

  // 处理支付状态回调
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');

    if (paymentStatus) {
      if (paymentStatus === 'success') {
        handlePaymentSuccess(urlParams);
        const { message, type } = getPaymentStatusMessage('success');
        addNotification({
          type,
          message,
          duration: 8000
        });
        
        // 刷新权限数据
        setTimeout(() => {
          refreshAccess();
        }, 2000);
      } else if (paymentStatus === 'cancel') {
        handlePaymentCancel(urlParams);
        const { message, type } = getPaymentStatusMessage('cancel');
        addNotification({
          type,
          message,
          duration: 5000
        });
      }

      // 清理URL参数
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [handlePaymentSuccess, handlePaymentCancel, getPaymentStatusMessage, addNotification, refreshAccess]);

  return (
    <>
      <Helmet>
        <title>Pricing - SleepCalc Pro</title>
        <meta name="description" content="Choose the perfect plan for your sleep optimization needs. Unlock advanced sleep tracking and personalized insights." />
        <meta name="keywords" content="sleep tracking, pricing, subscription, pro features, premium plan" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header Section */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">
              Choose Your Perfect Plan
            </h1>
            <p className="text-xl text-slate-600 dark:text-slate-400 mb-8 max-w-3xl mx-auto">
              Unlock advanced sleep optimization features tailored to your needs. 
              Start your journey to better sleep today.
            </p>

            {/* Current User Status */}
            {!isLoading && (
              <motion.div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-slate-700"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
              >
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Current Plan: <span className="font-semibold text-slate-900 dark:text-white">
                    {currentPlan === 'free' ? 'Free' : currentPlan === 'pro' ? 'Pro' : 'Premium'}
                  </span>
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-500">
                  ID: {userId.slice(0, 8)}...
                </span>
              </motion.div>
            )}
          </motion.div>

          {/* Free Tier Card */}
          <motion.div 
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-8">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 mb-4">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Free Forever</h2>
                </div>
                <p className="text-slate-600 dark:text-slate-400 mb-6">
                  Perfect for getting started with sleep optimization
                </p>
                <div className="grid md:grid-cols-2 gap-6 text-left">
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Included Features:</h4>
                    <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        Basic sleep calculator
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        Standard scenarios
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        Moon phase display
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        Basic tips and recommendations
                      </li>
                    </ul>
                  </div>
                  <div className="flex items-center justify-center">
                    <div className="text-6xl font-bold text-green-500">$0</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Paid Plans */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <UpgradeCard plan="pro" />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              <UpgradeCard plan="premium" featured />
            </motion.div>
          </div>

          {/* FAQ Section */}
          <motion.div 
            className="mt-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
                  Can I upgrade or downgrade anytime?
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Yes! You can change your plan at any time. Upgrades take effect immediately, 
                  and downgrades take effect at the end of your billing cycle.
                </p>
              </div>
              
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
                  Is there a money-back guarantee?
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  We offer a 30-day money-back guarantee. If you're not satisfied, 
                  we'll refund your payment, no questions asked.
                </p>
              </div>
              
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
                  What payment methods do you accept?
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  We accept all major credit cards, PayPal, and bank transfers 
                  through our secure payment processor.
                </p>
              </div>
              
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
                  Do you offer business plans?
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Yes! Our Premium plan includes white-label options perfect for businesses. 
                  Contact us for custom enterprise solutions.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
          >
            <div className="flex justify-center items-center gap-8 text-slate-500 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Secure Payments</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <span>30-Day Guarantee</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-blue-500" />
                <span>Instant Access</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

console.log('✅ PricingPage loaded successfully');